// POS print paper sizes.
//
// Mirrors admin-mobile-app/src/utils/paperSize.js. Keep the two byte-identical apart from
// the one header line naming the mirror direction, following the existing
// money.js / partyLabel.js / tenantTime.js / storeTicket.js duplication convention.
//
// WHY THIS FILE EXISTS. The paper size was a BINARY coerced inline at roughly fifteen call
// sites, every one of them shaped `=== 'a4' ? 'a4' : 'half_letter'`: two print builders,
// four "what size is configured" derivations, two save payloads, two defaults blocks, and
// the settings controls. Adding a third value by editing fifteen ternaries would have left
// any missed one silently rewriting the admin's choice back to half-letter -- which is
// exactly how `paperSize` was silently dropped by the backend whitelist before. The
// allowlist now lives once.
//
// The `@page` rule and the layout override live here too, because the repo already
// requires the web and mobile print builders to produce the same document for the same
// order, and they are independent hand-maintained copies in two very large files. Sharing
// the CSS makes that true by construction instead of by vigilance.
//
// Pure: no React, no native imports.

export const PAPER_HALF_LETTER = 'half_letter';
export const PAPER_A4 = 'a4';
//: 80 mm thermal receipt roll -- the standard POS receipt printer media.
export const PAPER_THERMAL_80 = 'thermal_80mm';

/** The default, and what every record written before paper size existed resolves to. */
export const DEFAULT_PAPER_SIZE = PAPER_HALF_LETTER;

/**
 * The sizes the admin may choose, in the order they are offered on both surfaces.
 *
 * `shortLabel` exists because mobile renders these as chips where the full label does not
 * fit; keeping both here stops the two surfaces inventing different names for one value.
 */
export const PAPER_SIZES = [
  {
    value: PAPER_HALF_LETTER,
    label: 'Half-letter (5.5 × 8.5 in)',
    shortLabel: 'HALF-LETTER',
  },
  {
    value: PAPER_A4,
    label: 'A4 (210 × 297 mm)',
    shortLabel: 'A4',
  },
  {
    value: PAPER_THERMAL_80,
    label: '80 mm thermal roll (POS receipt)',
    shortLabel: '80MM ROLL',
  },
];

const ALLOWED = PAPER_SIZES.map((p) => p.value);

/**
 * normalizePaperSize(raw) -> one of the allowed values
 *
 * Total function, defaulting to half-letter. Legacy records carry no `paperSize` at all
 * (the setting used to be an `orderDetailsOnly` boolean, which only ever produced
 * half-letter), so absent MUST resolve to half-letter or existing tenants would see their
 * prints change size on upgrade.
 *
 * Also accepts a few spellings a hand-edited record or an older client might carry, so a
 * near-miss degrades to the intended size rather than silently to half-letter.
 */
export function normalizePaperSize(raw) {
  const s = String(raw == null ? '' : raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!s) return DEFAULT_PAPER_SIZE;
  if (ALLOWED.includes(s)) return s;
  // Tolerated aliases. Deliberately narrow: anything else is a typo, and guessing at a
  // typo for a physical paper size wastes a roll.
  if (s === 'a4_210x297' || s === 'a_4') return PAPER_A4;
  if (
    s === 'thermal'
    || s === '80mm'
    || s === 'mm80'
    || s === 'thermal_80'
    || s === '80_mm'
    || s === 'thermal80mm'
    || s === 'receipt'
  ) {
    return PAPER_THERMAL_80;
  }
  if (s === 'halfletter' || s === 'half_letter_5_5x8_5') return PAPER_HALF_LETTER;
  return DEFAULT_PAPER_SIZE;
}

/** Human label for a stored value. Falls back through the normalizer. */
export function paperSizeLabel(raw) {
  const v = normalizePaperSize(raw);
  const found = PAPER_SIZES.find((p) => p.value === v);
  return found ? found.label : '';
}

/** Short label, for space-constrained controls. */
export function paperSizeShortLabel(raw) {
  const v = normalizePaperSize(raw);
  const found = PAPER_SIZES.find((p) => p.value === v);
  return found ? found.shortLabel : '';
}

/** True for continuous-roll media, where page height is not fixed. */
export function isRollPaper(raw) {
  return normalizePaperSize(raw) === PAPER_THERMAL_80;
}

/**
 * paperSizePageCss(raw) -> the `@page` rule for this size
 *
 * THE 80 MM NUMBERS, since they are not arbitrary: the roll is 80 mm wide but the print
 * head covers about 72 mm, so a 4 mm margin each side keeps content inside the printable
 * area instead of having the edges shaved off. Height is `auto` because a receipt roll is
 * continuous -- a fixed height would either pad every ticket with blank paper or cut a long
 * one off mid-item.
 */
export function paperSizePageCss(raw) {
  switch (normalizePaperSize(raw)) {
    case PAPER_A4:
      return '@page { size: A4; margin: 12mm; }';
    case PAPER_THERMAL_80:
      return '@page { size: 80mm auto; margin: 4mm; }';
    case PAPER_HALF_LETTER:
    default:
      return '@page { size: 5.5in 8.5in; margin: 0.35in; }';
  }
}

/**
 * paperSizeOverrideCss(raw) -> extra rules to append AFTER the builder's base styles
 *
 * '' for the two sheet sizes, so their output is byte-identical to before this size
 * existed -- that is the non-regression contract for every current tenant.
 *
 * For 80 mm it is not optional. The ticket's base layout is a two-column grid with 15-16px
 * type and fixed 36px/88px table columns, designed for a 139 mm sheet. Dropped onto 72 mm
 * of printable width that wraps into an unreadable mess, so the override collapses the grid
 * to one column, shrinks the type, and narrows the fixed columns. Setting `@page` alone
 * would produce correctly-sized paper carrying garbage.
 *
 * `!important` throughout because these are appended after the base rules but compete with
 * equally-specific selectors, and several base rules are inline-styled in the markup.
 */
/**
 * paperSizeBodyWidth(raw) -> the CSS width the RECEIPT builder locks its body to
 *
 * The receipt document pins an explicit body width so the itemized table cannot reflow
 * differently between the on-screen preview and the printed page.
 *
 * NOTE THE HALF-LETTER ANSWER IS US LETTER, not 5.5x8.5in. The receipt path has always
 * used a full 8.5x11 sheet for anything that is not A4 -- only the kitchen TICKET honours
 * half-letter. That asymmetry predates this module and is preserved deliberately: changing
 * it would resize every existing tenant's printed bill. It is surfaced here rather than
 * left buried in a ternary so nobody "fixes" it by accident.
 */
export function paperSizeBodyWidth(raw) {
  switch (normalizePaperSize(raw)) {
    case PAPER_A4:
      return '186mm';
    case PAPER_THERMAL_80:
      // 80mm roll less the 4mm margin each side.
      return '72mm';
    case PAPER_HALF_LETTER:
    default:
      return '7.5in';
  }
}

/**
 * paperSizeReceiptScaffoldCss(raw) -> `@page` plus the print-media width lock
 *
 * Only the RECEIPT builder needs this; the kitchen ticket uses `paperSizePageCss` alone.
 * Kept here rather than inline because it was a two-branch template literal that had to
 * become three, and the third branch is the one with unusual numbers.
 */
export function paperSizeReceiptScaffoldCss(raw) {
  const size = normalizePaperSize(raw);
  if (size === PAPER_A4) {
    return `@page { size: A4; margin: 12mm; }
          @media print {
            html { width: 210mm; }
            body {
              width: 186mm !important;
              margin: 0 !important;
              padding: 0 !important;
              transform: none !important;
              -webkit-transform: none !important;
            }
          }`;
  }
  if (size === PAPER_THERMAL_80) {
    // No fixed page height: a receipt roll is continuous, so the page grows with the
    // itemized list instead of being cut at a sheet boundary.
    return `@page { size: 80mm auto; margin: 4mm; }
          @media print {
            html { width: 80mm; }
            body {
              width: 72mm !important;
              margin: 0 !important;
              padding: 0 !important;
              transform: none !important;
              -webkit-transform: none !important;
            }
          }`;
  }
  return `@page { size: 8.5in 11in; margin: 0.5in; }
          @media print {
            html { width: 8.5in; height: 11in; }
            body {
              width: 7.5in !important;
              margin: 0 !important;
              padding: 0 !important;
              transform: none !important;
              -webkit-transform: none !important;
            }
          }`;
}

export function paperSizeOverrideCss(raw) {
  if (normalizePaperSize(raw) !== PAPER_THERMAL_80) return '';
  return `
            /* 80 mm thermal roll: ~72mm printable. Single column, tighter type.
               COVERS BOTH BUILDERS. The web and mobile receipt builders were written
               separately and share no class names: web uses a .grid / .details-grid layout
               with a bordered table (.col-num, .col-qty, th, td) at 15-16px, while mobile
               uses a .wrap container with flex .row pairs at 12px. Selectors belonging to
               the other document are simply inert, so one override can serve both and the
               same order still prints the same shape on each surface. */

            /* --- shared --- */
            body { font-size: 12px !important; }
            /* Both two-column grids in the print builders: .grid on the kitchen ticket and
               .details-grid on the itemized receipt. The receipt additionally re-forces two
               columns inside an @media print block, which is why these carry !important --
               an important declaration beats a non-important one even inside a media query.
               (No backticks in this comment: it lives inside a template literal.) */
            .grid { display: block !important; }
            .details-grid { display: block !important; grid-template-columns: 1fr !important; }
            .grid > .cell, .details-grid > .cell, .details-grid > .line-row { margin-bottom: 6px !important; }
            .cell { gap: 6px !important; }
            .section-title { font-size: 13px !important; margin-bottom: 6px !important; }
            .hr { margin: 8px 0 !important; border-top-width: 1px !important; }
            table { table-layout: fixed !important; width: 100% !important; }
            th { font-size: 11px !important; padding: 4px 2px !important; border-bottom-width: 1px !important; }
            td { font-size: 12px !important; padding: 5px 2px !important; }
            .col-num { width: 16px !important; }
            .col-qty { width: 32px !important; text-align: right !important; }
            .item-notes { margin-top: 3px !important; font-size: 11px !important; }
            .item-name-en { font-size: 0.8em !important; }
            .write-line { height: 16px !important; margin-top: 6px !important; }

            /* --- mobile builder vocabulary --- */
            /* 14px of padding each side costs ~7.4mm of a 72mm width, which is most of a
               column of text on a receipt. */
            .wrap { padding: 6px !important; }
            .title { font-size: 14px !important; line-height: 1.15 !important; }
            .logo { width: 32px !important; height: 32px !important; }
            .brand { gap: 6px !important; }
            .rule { margin: 8px 0 !important; }
            .item { padding: 6px 0 !important; }
            .item-top { gap: 6px !important; }
            .qty { min-width: 20px !important; }
            .notes { font-size: 11px !important; }
            .footer { margin-top: 8px !important; font-size: 10px !important; }

            /* Long unbroken strings (order numbers, emails, addresses) must wrap rather
               than push the layout wider than the paper. Both vocabularies listed. */
            body, td, th, .cell, .fill, .name, .v, .k, .notes { overflow-wrap: anywhere !important; }
  `;
}
